import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import './StatsBar.css';

/**
 * Animated count-up hook.
 * Counts from 0 to `target` over `duration` ms with easeOut.
 */
function useCountUp(target, duration = 1200, enabled = false) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const prevTarget = useRef(0);

  useEffect(() => {
    if (!enabled || target === 0) return;

    const from = prevTarget.current;
    prevTarget.current = target;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutQuart
      const ease = 1 - Math.pow(1 - progress, 4);
      setDisplay(Math.round(from + (target - from) * ease));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, enabled]);

  return display;
}

/** Format number with commas: 1247 → "1,247" */
function fmt(n) {
  return n.toLocaleString();
}

export default function StatsBar() {
  const [stats, setStats] = useState(null);
  const [visible, setVisible] = useState(false);
  const barRef = useRef(null);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/designs?limit=1&sort=recent`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.total !== undefined || data.totalLikes !== undefined) {
        setStats({
          designs: data.total ?? 0,
          likes: data.totalLikes ?? 0,
        });
      }
    } catch {
      // silently ignore — non-critical UI
    }
  };

  // Fetch on mount + refresh every 60s
  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60_000);
    return () => clearInterval(interval);
  }, []);

  // IntersectionObserver — trigger count-up when bar scrolls into view
  useEffect(() => {
    if (!barRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    observer.observe(barRef.current);
    return () => observer.disconnect();
  }, []);

  const designCount = useCountUp(stats?.designs ?? 0, 1400, visible && !!stats);
  const likeCount = useCountUp(stats?.likes ?? 0, 1600, visible && !!stats);

  // Don't render until we have real data
  if (!stats) return null;

  return (
    <div className="stats-bar" ref={barRef} aria-label="Community statistics">
      <div className="stats-bar-inner">
        <div className="stat-item">
          <span className="stat-icon">🎨</span>
          <span className="stat-value">{fmt(designCount)}</span>
          <span className="stat-label">designs created</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-icon">❤️</span>
          <span className="stat-value">{fmt(likeCount)}</span>
          <span className="stat-label">likes given</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-icon">🌍</span>
          <span className="stat-value stat-value--text">Made by</span>
          <span className="stat-label">creators worldwide</span>
        </div>
      </div>
    </div>
  );
}
